import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Flower2, TreePine, Wheat, Sprout, Cloud } from "lucide-react";

export function usePollen() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/pollen").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 1800_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

const ICONS = { Trees: TreePine, Grass: Wheat, Weeds: Sprout, Mold: Cloud };
const SUB = { Trees: "Tree pollen", Grass: "Grass pollen", Weeds: "Weed pollen", Mold: "Mold spores" };
const clamp2 = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };

function Cat({ cat }) {
  const Icon = ICONS[cat.name] || Flower2;
  const pct = Math.max(4, Math.min(100, cat.needle ?? 4));
  return (
    <div
      className="flex flex-col rounded-[6px] bg-white px-5 py-4 shadow-[0_12px_30px_rgba(10,15,28,0.12)]"
      style={{ borderLeft: `10px solid ${cat.color}` }}
      data-testid={`pollen-cat-${cat.name.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-oswald text-[28px] font-black uppercase tracking-wide text-[#0a0f1c]">
          <Icon className="h-6 w-6" style={{ color: cat.color }} /> {cat.name}
        </div>
        <span className="rounded-[3px] px-3 py-1 font-oswald text-[19px] font-bold uppercase tracking-wider text-white" style={{ background: cat.color }} data-testid={`pollen-cat-level-${cat.name.toLowerCase()}`}>
          {cat.level_label}
        </span>
      </div>
      <div className="mt-3 h-[14px] w-full overflow-hidden rounded-full bg-[#e6ebf2]">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: cat.color }} />
      </div>
      <div className="mt-3 font-oswald text-[19px] font-bold uppercase tracking-[0.22em] text-[#5b6675]">{SUB[cat.name]}</div>
      {cat.contributors?.length ? (
        <div className="mt-0.5 font-oswald text-[21px] font-semibold leading-snug text-[#2c3542]" style={clamp2}>{cat.contributors.join(", ")}</div>
      ) : null}
    </div>
  );
}

export function PollenSlide({ pollen }) {
  const cats = pollen?.categories || [];
  if (!cats.length || pollen?.count == null) return null;
  return (
    <div data-testid="pollen-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(135deg, #f4f6fa 0%, #dfe6ef 100%)" }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)" }} />
      <div className="relative grid h-full w-full grid-cols-[38%_1fr] gap-6 px-[48px] py-[20px]">
        <div className="flex flex-col justify-center rounded-[8px] bg-white px-8 py-7 shadow-[0_18px_40px_rgba(10,15,28,0.16)]" style={{ borderLeft: `14px solid ${pollen.color}` }}>
          <div className="flex items-center gap-2 font-oswald text-[21px] font-bold uppercase tracking-[0.3em] text-[#5b6675]">
            <Flower2 className="h-6 w-6" style={{ color: pollen.color }} /> Total Pollen Count
          </div>
          <div className="font-oswald text-[150px] font-black leading-[0.82] text-[#0a0f1c]" data-testid="pollen-count">{pollen.count}</div>
          <div className="font-oswald text-[22px] font-semibold uppercase tracking-wider text-[#5b6675]">grains / m³ · {pollen.station}</div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="rounded-[5px] px-5 py-2 font-oswald text-[34px] font-black uppercase tracking-wide text-white" style={{ background: pollen.color }} data-testid="pollen-level">{pollen.level_label}</span>
            {pollen.primary ? <span className="font-oswald text-[22px] font-bold uppercase text-[#0a0f1c]">Driven by {pollen.primary}</span> : null}
          </div>
          <div className="mt-5 font-oswald text-[24px] font-semibold leading-snug text-[#2c3542]">{pollen.guidance}</div>
        </div>
        <div className="grid grid-cols-2 grid-rows-2 gap-4">
          {cats.slice(0, 4).map((c) => <Cat key={c.name} cat={c} />)}
        </div>
      </div>
    </div>
  );
}
